import { Image } from 'expo-image'
import { useEffect, useState } from 'react'
import { View, Platform, Text, ScrollView, TouchableOpacity } from 'react-native'
import theme from '../../../theme'
import * as Progress from 'react-native-progress'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { Card } from './CardComponent'
import { LineChartCard, ComparisonBarChart, SimpleDataBarChartCard, VerticalStackedBars } from './DotiCharts'
import { request_get_doti } from '../../../src/services/doti.services'
import { Loader } from '../customUtils/Components/Loader'
import { getAuthToken } from '../..//utils/functions.utils'

const itemsSpacing = 16

export const DotiScreen = () => {
    const [listView, setListView] = useState(false)
    const containerWidth = listView ? '100%' : '50%'
    const [data, setData] = useState({ left: [], right: [], singleLine: [] })
    const [loading, setLoading] = useState(true)

    const fetchData = async () => {
        try {
            const authToken = await getAuthToken()
            const data = await request_get_doti({ authToken, is_gamification: true })
            if (data && data.single) {
                let cards = data.single.cards ?? []
                const leftCards = cards.filter((c) => c.right === false)
                const rightCards = cards.filter((c) => c.right === true)
                const singleLineCards = data.single.singleLine ? data.single.singleLine : []
                setData({ left: leftCards, right: rightCards, singleLine: singleLineCards })
            }
            setLoading(false)
        } catch (error) {
            console.error('Error fetching data:', error)
            setLoading(false)
        } finally {
            setLoading(false)
        }
    }
    useEffect(() => {
        fetchData()
    }, [])
    const noData = data.left.length === 0 && data.right.length === 0 && data.singleLine.length === 0
    if (loading) {
        return <Loader />
    }
    if (noData) {
        return <Text style={{ flex: 1, justifyContent: 'center', alignContent: 'center' }}>No hay datos disponibles para mostrar</Text>
    }
    return (
        <ScrollView style={{ flex: 1, backgroundColor: '#F1F2FF' }}>
            <View style={{ width: '100%', flexDirection: 'row', paddingHorizontal: 12, paddingVertical: 16 }}>
                <Image source={require('../../../assets/doti/doti.png')} style={{ width: 44, height: 44 }} />
                <View style={{ flex: 1, marginStart: 12, marginTop: 5 }}>
                    <Text style={{ fontSize: 15, fontWeight: '700', color: theme.colors.textPrimary }}>Hola, Usuario!</Text>
                    <Text style={{ fontSize: 13, fontWeight: '400', color: theme.colors.darkGray, marginTop: 4 }}>¿En que puedo ayudarte?</Text>
                </View>

                <TouchableOpacity style={{ padding: 8, borderRadius: 12, borderWidth: 1, borderColor: '#E1E3FF', marginEnd: 12 }}>
                    <Image source={require('../../../assets/doti/chats.png')} style={{ width: 24, height: 24 }} />
                </TouchableOpacity>
                {/* ListButton */}
                <TouchableOpacity
                    onPress={() => setListView(true)}
                    style={[
                        {
                            padding: 8,
                            borderTopStartRadius: 12,
                            borderBottomStartRadius: 12,
                            borderWidth: 1,
                            borderRightWidth: 0,
                            borderColor: '#E1E3FF',
                        },
                        listView && { backgroundColor: '#E1E3FF' },
                    ]}
                >
                    <Image
                        source={require('../../../assets/doti/cardsthree.png')}
                        style={[{ width: 24, height: 24, tintColor: 'black' }, listView && { tintColor: theme.colors.colorPrimary }]}
                    />
                </TouchableOpacity>
                {/* GridButton */}
                <TouchableOpacity
                    onPress={() => setListView(false)}
                    style={[
                        {
                            padding: 8,
                            borderTopEndRadius: 12,
                            borderBottomEndRadius: 12,
                            borderWidth: 1,
                            borderLeftWidth: 0,
                            borderColor: '#E1E3FF',
                            marginEnd: 4,
                        },
                        !listView && { backgroundColor: '#E1E3FF' },
                    ]}
                >
                    <Image
                        source={require('../../../assets/doti/squarefour.png')}
                        style={[{ width: 24, height: 24, tintColor: 'black' }, !listView && { tintColor: theme.colors.colorPrimary }]}
                    />
                </TouchableOpacity>
            </View>

            <View style={{ flexDirection: listView ? 'column' : 'row', margin: itemsSpacing }}>
                <View style={[{ width: containerWidth, gap: itemsSpacing, paddingEnd: itemsSpacing / 2 }, listView && { paddingEnd: 0 }]}>
                    {data.left.length > 0 && data.left.map((item, index) => <DotiItem key={index} item={item} listView={listView} />)}
                </View>

                <View
                    style={[
                        { width: containerWidth, gap: itemsSpacing, paddingStart: itemsSpacing / 2 },
                        listView && { paddingStart: 0, paddingTop: itemsSpacing },
                    ]}
                >
                    {data.right.length > 0 && data.right.map((item, index) => <DotiItem key={index} item={item} listView={listView} />)}
                </View>
            </View>
            {data.singleLine.length > 0 && data.singleLine.map((item, index) => <DotiItem key={index} item={item} listView={listView} />)}
        </ScrollView>
    )
}

export const DotiItem = ({ item, listView }) => {
    switch (item.type) {
        case 'TasksCard':
            return <TasksCard data={item.values ?? []} />
        case 'CircleStatCard':
            return <CircleStatCard title={item.title} value={item.value} subtitle={item.description} change={item.change} />
        case 'CoursesCard':
            return <CoursesCard completed={item.completed} total={item.value} />
        case 'LineChartCard':
            return <LineChartCard title={item.title} value={item.value} data={item.values} />
        case 'DeadlineCard':
            return (
                <DeadlineCard
                    title={item.title}
                    value={item.value}
                    description={item.description}
                    change={item.change}
                    descriptionColor={item.progressColor}
                />
            )
        case 'OpportunityCard':
            return <OpportunityCard title={item.title} data={item.values} />
        case 'SimpleDataBarChartCard':
            return <SimpleDataBarChartCard title={item.title} data={item.values} styles={{ marginHorizontal: itemsSpacing }} />
        case 'ComparisonBarChart':
            return <ComparisonBarChart styles={{ margin: itemsSpacing }} title={item.title} data={item.values} />
        case 'VerticalStackedBars':
            return (
                <VerticalStackedBars
                    styles={{ marginHorizontal: itemsSpacing, marginBottom: itemsSpacing * 2 }}
                    title={item.title}
                    data={item.values}
                />
            )
        case 'GroupCards':
            return <MultipleProgressCard cards={item.cards ?? []} listView={listView} />
        default:
            return <></>
    }
}
export const TasksCard = ({ data }) => {
    return (
        <Card>
            {data.map((item, index) => (
                <View key={index} style={{ flexDirection: 'row', marginBottom: index != data.length - 1 ? 8 : 0 }}>
                    <Text
                        style={{
                            fontWeight: '700',
                            color: theme.colors.colorPrimary,
                            marginRight: 6,
                        }}
                    >
                        {index + 1}.
                    </Text>

                    <Text style={{ flex: 1, color: theme.colors.textPrimary, fontWeight: '400' }}>{item.value}</Text>
                </View>
            ))}
        </Card>
    )
}

export const DeadlineCard = ({ title, value, description, change = null, descriptionColor, styles }) => {
    const descColor = descriptionColor && descriptionColor != '' ? descriptionColor : theme.colors.colorPrimary
    return (
        <Card style={styles}>
            <Text
                style={{
                    fontSize: 15,
                    fontWeight: '600',
                    marginBottom: 10,
                    color: theme.colors.textPrimary,
                }}
            >
                {title}
            </Text>

            <Text
                style={{
                    fontSize: 42,
                    fontWeight: '700',
                    color: '#5758FF',
                }}
            >
                {value}
            </Text>

            <Text
                style={{
                    marginTop: 0,
                    fontSize: 15,
                }}
            >
                {change && (
                    <Text
                        style={{
                            color: change > 0 ? 'green' : 'red',
                            fontWeight: '600',
                        }}
                    >
                        {change > 0 ? `+${change}% ` : `${change}% `}
                    </Text>
                )}
                <Text style={{ color: descColor, fontWeight: '400' }}>{description}</Text>
            </Text>
        </Card>
    )
}

export const CircleStatCard = ({
    style,
    title,
    value = 0,
    subtitle,
    change = null,
    showCard = true,
    progressColor = null,
    unfilledColor = '#D0D2E7',
    verySmall = false,
}) => {
    let Parent = Card
    if (showCard === false) Parent = View
    const progressText = value ? `${value.toFixed(0)}%` : '0%'
    const percentage = value / 100
    let newColor = progressColor && progressColor != '' ? progressColor : theme.colors.colorPrimary
    return (
        <Parent style={style}>
            <View style={{ alignItems: 'center', marginBottom: 10 }}>
                {/* Círculo (simplificado) */}

                <Progress.Circle
                    size={!verySmall ? 88 : 80}
                    progress={percentage}
                    showsText={true}
                    formatText={(progress) => progressText}
                    textStyle={{
                        fontSize: !verySmall ? 20 : 18,
                        fontWeight: '700',
                        color: theme.colors.textPrimary,
                    }}
                    thickness={6}
                    color={newColor}
                    unfilledColor={unfilledColor}
                    borderWidth={0}
                    strokeCap='round'
                />
            </View>

            <Text
                style={{
                    textAlign: 'center',
                    fontSize: !verySmall ? 15 : 14,
                    fontWeight: '600',
                    color: theme.colors.textPrimary,
                    marginBottom: change ? 6 : 0,
                }}
            >
                {title}
            </Text>

            {change ? (
                <Text
                    style={{
                        textAlign: 'center',
                        color: change > 0 ? 'green' : 'red',
                        fontWeight: '600',
                    }}
                >
                    {change > 0 ? `+${change}%` : `${change}%`}
                    <Text style={{ color: theme.colors.textPrimary, fontWeight: '400' }}>{' ' + subtitle}</Text>
                </Text>
            ) : null}

            {!change && subtitle && <Text style={{ textAlign: 'center', color: '#7B7B7B' }}>{subtitle}</Text>}
        </Parent>
    )
}

export const CoursesCard = ({ style, completed = 0, total = 0, showCard = true, verySmall = false, progressColor = null }) => {
    let Parent = Card
    if (showCard === false) Parent = View
    let value = 0
    let percentage = 0
    if (total > 0) {
        value = (completed * 100) / total
    }
    percentage = value / 100
    const progressText = completed + ' de ' + total
    const newColor = progressColor && progressColor != '' ? progressColor : '#009E4D'
    return (
        <Parent style={style}>
            <View style={{ alignItems: 'center' }}>
                <Progress.Circle
                    size={!verySmall ? 88 : 80}
                    progress={percentage}
                    showsText={true}
                    formatText={(progress) => progressText}
                    textStyle={{
                        fontSize: !verySmall ? 20 : 18,
                        fontWeight: '700',
                        color: theme.colors.textPrimary,
                    }}
                    thickness={6}
                    color={newColor}
                    unfilledColor={'#D2EBD7'}
                    borderWidth={0}
                    strokeCap='round'
                />
                {/* <Text style={{ fontSize: 22, fontWeight: '700', color: '#' }}>
                       
                    </Text> */}
                <Text
                    style={{
                        textAlign: 'center',
                        marginTop: 10,
                        fontSize: !verySmall ? 15 : 14,
                        fontWeight: '600',
                        color: theme.colors.textPrimary,
                        flex: 1,
                    }}
                >
                    {'Cursos completados \neste mes'}
                </Text>
            </View>
        </Parent>
    )
}

export const OpportunityCard = ({ title, data }) => {
    return (
        <Card>
            <Text
                style={{
                    fontSize: 15,
                    fontWeight: '600',
                    color: theme.colors.textPrimary,
                    marginBottom: 10,
                }}
            >
                {title}
            </Text>
            {data.map((item, index) => (
                <View key={index} style={{ marginBottom: 24, flex: 1 }}>
                    <View
                        style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            marginBottom: 6,
                        }}
                    >
                        <Text
                            style={{
                                fontSize: 15,
                                color: '#1A1A1A',
                                maxWidth: '60%',
                            }}
                            ellipsizeMode='tail'
                            numberOfLines={1}
                        >
                            {item.name}
                        </Text>

                        <View
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 0,
                            }}
                        >
                            {item.percent && item.percent < 40 && <MaterialCommunityIcons name='alert' size={18} color='#E54335' />}
                            <Text
                                style={{
                                    fontSize: 16,
                                    fontWeight: '600',
                                    color: item.percent < 40 ? '#E54335' : theme.colors.colorPrimary,
                                }}
                            >
                                {item.percent}%
                            </Text>
                        </View>
                    </View>

                    {/* Barra de progreso */}
                    <View
                        style={{
                            height: 8,
                            backgroundColor: '#D8D6E5',
                            borderRadius: 8,
                            flex: 1,
                        }}
                    >
                        <View
                            style={[
                                {
                                    height: 8,
                                    backgroundColor: item.percent < 40 ? '#E54335' : theme.colors.colorPrimary,
                                    borderRadius: 8,
                                },
                                { width: `${item.percent}%` },
                            ]}
                        />
                    </View>
                </View>
            ))}
        </Card>
    )
}

export const MultipleProgressCard = ({ cards, listView }) => {
    const [cardWidth, setCardWidth] = useState(0)
    const verySmall = listView && cardWidth < 370
    const containerMaxWidth = verySmall ? 100 : 120
    if (!cards || cards.length === 0) {
        return <></>
    }
    return (
        <Card
            style={{
                flexDirection: listView ? 'row' : 'column',
                paddingHorizontal: 10,
                justifyContent: listView ? 'space-evenly' : 'center',
                alignContent: 'center',
            }}
            setCardWidth={(w) => {
                setCardWidth(w)
            }}
        >
            {cards.length > 0 &&
                cards.map((item, index) => {
                    let view = null
                    let last = index === cards.length - 1
                    switch (item.type) {
                        case 'CircleStatCard':
                            view = (
                                <CircleStatCard
                                    style={{ maxWidth: listView ? containerMaxWidth : undefined }}
                                    title={item.title}
                                    value={item.value}
                                    showCard={false}
                                    verySmall={verySmall}
                                    progressColor={item.progressColor}
                                />
                            )
                            break
                        case 'CoursesCard':
                            view = (
                                <CoursesCard
                                    style={{ maxWidth: listView ? containerMaxWidth : undefined }}
                                    completed={item.completed}
                                    total={item.value}
                                    showCard={false}
                                    verySmall={verySmall}
                                />
                            )
                            break
                        default:
                            return <></>
                    }
                    return (
                        <>
                            {view}
                            {!last && !listView && <View style={[{ height: 20, width: 0 }]} />}
                        </>
                    )
                })}
        </Card>
    )
}
